import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '../services/api'
import { useNotificationStore } from './notifications'

/**
 * Authentication Store
 *
 * Este store maneja el estado de autenticación del usuario.
 */
export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const token = ref(localStorage.getItem('token') || null)
  const loading = ref(false)
  const error = ref(null)

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const userRole = computed(() => user.value?.rol_name || null)
  const userEmail = computed(() => user.value?.email || null)
  const userName = computed(() => user.value?.fullname || user.value?.email || 'Usuario')

  // Actions
  const setToken = (newToken) => {
    token.value = newToken
    if (newToken) {
      localStorage.setItem('token', newToken)
    } else {
      localStorage.removeItem('token')
    }
  }

  const setUser = (userData) => {
    user.value = userData
  }

  const setLoading = (isLoading) => {
    loading.value = isLoading
  }

  const setError = (errorMessage) => {
    error.value = errorMessage
  }

  const clearError = () => {
    error.value = null
  }

  /**
   * Login
   * Autentica al usuario con email y contraseña
   *
   * @param {string} email - Email del usuario
   * @param {string} password - Contraseña del usuario
   * @param {boolean} rememberMe - Si se debe recordar la sesión
   * @returns {Promise<boolean>} - True si el login fue exitoso
   */
  const login = async (email, password, rememberMe = false) => {
    const notificationStore = useNotificationStore()
    setLoading(true)
    clearError()

    try {
      // Call authentication API
      const response = await authApi.login(email, password)

      // Store token
      setToken(response.access_token)

      // Store user data
      setUser(response.user)

      // Show success notification
      notificationStore.addNotification('success', `Bienvenido, ${response.user.fullname || response.user.email}`)

      console.log('User logged in successfully:', response.user.email)

      return true
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Error al iniciar sesión'
      setError(errorMsg)

      // Show error notification
      notificationStore.addNotification('error', errorMsg)

      console.error('Login error:', errorMsg)
      throw new Error(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Logout
   * Limpia la sesión del usuario
   */
  const logout = () => {
    setToken(null)
    setUser(null)
    clearError()
    console.log('User logged out')
  }

  /**
   * Check Authentication
   * Verifica si hay un token válido y carga los datos del usuario
   *
   * @returns {Promise<boolean>} - True si el usuario está autenticado
   */
  const checkAuth = async () => {
    if (!token.value) {
      return false
    }

    setLoading(true)

    try {
      // Verify token with backend and get user data
      const userData = await authApi.getCurrentUser()
      setUser(userData)

      console.log('Token verified, user authenticated:', userData.email)
      return true
    } catch (err) {
      // Si el token es inválido o expiró, limpiar la sesión
      console.error('Auth check failed:', err.message)
      logout()
      return false
    } finally {
      setLoading(false)
    }
  }

  /**
   * Refresh Token
   * Renueva el token de acceso antes de que expire
   *
   * TODO: Implementar lógica de refresh token
   */
  const refreshToken = async () => {
    try {
      // TODO: Implementar refresh token
      // const response = await authApi.refreshToken()
      // setToken(response.token)
      console.log('Token refresh - To be implemented')
    } catch (err) {
      console.error('Error refreshing token:', err)
      logout()
    }
  }

  /**
   * Update User Profile
   * Actualiza la información del usuario
   *
   * @param {Object} userData - Datos del usuario a actualizar
   */
  const updateUser = (userData) => {
    if (user.value) {
      user.value = { ...user.value, ...userData }
    }
  }

  return {
    // State
    user,
    token,
    loading,
    error,

    // Getters
    isAuthenticated,
    userRole,
    userEmail,
    userName,

    // Actions
    login,
    logout,
    checkAuth,
    refreshToken,
    updateUser,
    setToken,
    setUser,
    clearError
  }
})
